import React from 'react';
import { findNextFocusableElement } from '@helpers/utils/a11y/focusable-elements';
import { useTranslation } from 'next-i18next';
import styles from './skip-link.module.scss';

/**
 * Delay (in ms) to wait for lazy-loaded content to stabilize before scrolling.
 *
 * Why this is needed:
 * - Product sliders and other sections may lazy-load when user scrolls near them
 * - These components include their own skip-links for keyboard navigation
 * - If we scroll immediately, we might skip past content that's about to load
 * - This delay allows lazy content to render, then we re-check for newly loaded skip-links
 * - If a new skip-link appears, we focus that instead of skipping past it
 *
 * Value rationale:
 * - Typical React component mount + render: ~200-300ms
 * - IntersectionObserver threshold + callback: ~50-100ms
 * - 300ms provides good balance between UX (not too slow) and reliability (catches lazy content)
 *
 * Note: In production (optimized builds), lazy content typically loads faster than in local dev.
 * This value is tuned for production performance.
 *
 * @internal - Exported for testing purposes
 */
export const LAZY_CONTENT_STABILIZATION_MS = 300;

interface SkipLinkProps {
  label: string;
  targetId: string;
  onSkip?: () => void;
  /**
   * Whether to wait for lazy-loaded content before scrolling.
   *
   * Set to `true` when skip-links are near lazy-loaded sections that might load
   * additional skip-links after activation (e.g., product sliders that lazy-load below the fold).
   *
   * Set to `false` (default) for static content where no additional skip-links will appear.
   *
   * @default false
   */
  awaitLazyContent?: boolean;
}

interface SkipTargetProps {
  id: string;
  label?: string;
}

/**
 * SkipLink - Creates a skip link that keyboard users can activate to bypass content
 * Skip links are keyboard-only (no mouse/click support) as mouse users can visually navigate
 * @param label - Text for the skip link (e.g., "Skip product slider (16 items)")
 * @param targetId - ID of the skip target element
 * @param onSkip - Optional callback when skip is activated
 */
export const SkipLink: React.FC<SkipLinkProps> = ({ label, targetId, onSkip, awaitLazyContent = false }) => {
  const handleSkip = (e: React.KeyboardEvent<HTMLAnchorElement>) => {
    e.preventDefault(); // Prevent default behavior (especially important for Space to prevent page scroll)

    const target = document.getElementById(targetId);
    if (!target) {
      return;
    }

    // Focus the target first (for consistency)
    target.focus();

    // Immediately advance to next visible focusable element
    const initialNextFocusable = findNextFocusableElement(target);

    if (initialNextFocusable) {
      // Prevent browser's default scroll behavior - we'll handle scrolling explicitly
      // This avoids double-scroll (browser auto-scroll + our scrollIntoView)
      initialNextFocusable.focus({ preventScroll: true });

      if (awaitLazyContent) {
        // Wait for lazy content to load/stabilize before scrolling
        // This prevents skipping over content that loads between activation and scroll
        setTimeout(() => {
          // Re-search for next focusable element (might have changed due to lazy loading)
          const finalNextFocusable = findNextFocusableElement(target);

          // If a different element was found (e.g., newly loaded skip-link), focus that instead
          if (finalNextFocusable && finalNextFocusable !== initialNextFocusable) {
            finalNextFocusable.focus({ preventScroll: true });
          }

          // Now scroll to whatever is focused (single, controlled scroll)
          const elementToScroll = document.activeElement as HTMLElement;
          elementToScroll.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }, LAZY_CONTENT_STABILIZATION_MS);
      } else {
        // No lazy content expected - scroll immediately (single, controlled scroll)
        initialNextFocusable.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }

    onSkip?.();
  };

  return (
    <a
      href="#"
      className={styles.skipLink}
      onKeyDown={(e) => {
        // Only respond to keyboard (Enter/Space), not mouse clicks
        // Mouse users can visually navigate and don't need skip links
        if (e.key === 'Enter' || e.code === 'Space') {
          handleSkip(e);
        }
      }}
    >
      {label}
    </a>
  );
};

/**
 * SkipTarget - Marks the end of a skippable section
 * Place this AFTER the content you want to skip
 * @param id - Unique ID that matches the SkipLink's targetId
 * @param label - Optional label for screen readers. Defaults to "End of the section" for consistency across all skip targets
 */
export const SkipTarget: React.FC<SkipTargetProps> = ({ id, label }) => {
  const { t: tA11y } = useTranslation('a11y');
  const ariaLabel = label ?? tA11y('A11Y.SKIP_END_SECTION');
  return (
    <div
      id={id}
      tabIndex={-1}
      className={styles.skipTarget}
      aria-label={ariaLabel}
    />
  );
};

export default SkipLink;
